// ============================================================================
// CropOverlay — draggable/resizable crop region rendered over an image
//
// Key: The overlay constrains itself to the actual rendered image area
// (accounting for object-fit: contain letterboxing), not the full container.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import type { CropRect } from '../utils/cropUtils';
import { clamp, clientToPercent, enforceAspectRatio, getContainedRectFromSize, MIN_CROP_PERCENT } from '../utils/cropUtils';

interface CropOverlayProps {
  imageRef: React.RefObject<HTMLImageElement>;
  crop: CropRect;
  onChange: (crop: CropRect) => void;
  aspectRatio: number | null;
  disabled?: boolean;
  naturalWidth: number;
  naturalHeight: number;
}

type DragMode =
  | { type: 'move'; startCrop: CropRect; startX: number; startY: number }
  | { type: 'resize'; handle: HandlePosition; startCrop: CropRect; startX: number; startY: number };

type HandlePosition = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

const HANDLE_SIZE = 10;
const EDGE_HANDLE_SIZE = 8;

const HANDLE_CURSORS: Record<HandlePosition, string> = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
};

function CornerHandle({
  position,
  disabled,
  onPointerDown,
}: {
  position: HandlePosition;
  disabled: boolean;
  onPointerDown: (e: React.PointerEvent, handle: HandlePosition) => void;
}) {
  const posStyles: Record<string, string | number> = {};
  if (position.includes('n')) posStyles.top = -HANDLE_SIZE / 2;
  if (position.includes('s')) posStyles.bottom = -HANDLE_SIZE / 2;
  if (position.includes('w')) posStyles.left = -HANDLE_SIZE / 2;
  if (position.includes('e')) posStyles.right = -HANDLE_SIZE / 2;

  return (
    <Box
      gc-testing-id={`crop-handle-${position}`}
      position="absolute"
      w={`${HANDLE_SIZE}px`}
      h={`${HANDLE_SIZE}px`}
      bg="white"
      border="1px solid"
      borderColor="gray.700"
      cursor={disabled ? 'default' : HANDLE_CURSORS[position]}
      pointerEvents={disabled ? 'none' : 'auto'}
      zIndex={3}
      {...posStyles}
      onPointerDown={(e) => !disabled && onPointerDown(e, position)}
    />
  );
}

function EdgeHandle({
  position,
  disabled,
  onPointerDown,
}: {
  position: HandlePosition;
  disabled: boolean;
  onPointerDown: (e: React.PointerEvent, handle: HandlePosition) => void;
}) {
  const posStyles: Record<string, string | number> = {};
  if (position === 'n') {
    posStyles.top = -EDGE_HANDLE_SIZE / 2;
    posStyles.left = '50%';
    posStyles.transform = 'translateX(-50%)';
  } else if (position === 's') {
    posStyles.bottom = -EDGE_HANDLE_SIZE / 2;
    posStyles.left = '50%';
    posStyles.transform = 'translateX(-50%)';
  } else if (position === 'w') {
    posStyles.left = -EDGE_HANDLE_SIZE / 2;
    posStyles.top = '50%';
    posStyles.transform = 'translateY(-50%)';
  } else {
    posStyles.right = -EDGE_HANDLE_SIZE / 2;
    posStyles.top = '50%';
    posStyles.transform = 'translateY(-50%)';
  }

  return (
    <Box
      gc-testing-id={`crop-handle-${position}`}
      position="absolute"
      w={`${EDGE_HANDLE_SIZE}px`}
      h={`${EDGE_HANDLE_SIZE}px`}
      bg="white"
      border="1px solid"
      borderColor="gray.700"
      cursor={disabled ? 'default' : HANDLE_CURSORS[position]}
      pointerEvents={disabled ? 'none' : 'auto'}
      zIndex={3}
      {...posStyles}
      onPointerDown={(e) => !disabled && onPointerDown(e, position)}
    />
  );
}

// ---------------------------------------------------------------------------
// Compute the pixel offset/size of the rendered image inside its container
// so the overlay is positioned exactly over the visible image, not the
// container (which may have letterbox/pillarbox padding).
// ---------------------------------------------------------------------------
function useImageBounds(
  imageRef: React.RefObject<HTMLImageElement>,
  naturalWidth: number,
  naturalHeight: number,
) {
  const [bounds, setBounds] = useState({ offsetX: 0, offsetY: 0, visibleW: 0, visibleH: 0 });

  const recalc = useCallback(() => {
    const img = imageRef.current;
    if (!img || naturalWidth <= 0 || naturalHeight <= 0) return;
    // Use offsetWidth/Height (layout-space, unaffected by CSS transforms)
    // instead of getBoundingClientRect (screen-space, scaled by zoom).
    // The overlay is positioned inside the same transform wrapper, so it
    // needs layout-space coordinates.
    const contained = getContainedRectFromSize(
      img.offsetWidth,
      img.offsetHeight,
      naturalWidth,
      naturalHeight,
    );
    setBounds({
      offsetX: img.offsetLeft + contained.offsetX,
      offsetY: img.offsetTop + contained.offsetY,
      visibleW: contained.visibleW,
      visibleH: contained.visibleH,
    });
  }, [imageRef, naturalWidth, naturalHeight]);

  useEffect(() => {
    recalc();
    // Recalc on resize
    const observer = new ResizeObserver(recalc);
    if (imageRef.current) observer.observe(imageRef.current);
    return () => observer.disconnect();
  }, [recalc, imageRef]);

  return bounds;
}

export function CropOverlay({
  imageRef,
  crop,
  onChange,
  aspectRatio,
  disabled = false,
  naturalWidth,
  naturalHeight,
}: CropOverlayProps) {
  const dragRef = useRef<DragMode | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Track the actual rendered image bounds inside the container
  const imageBounds = useImageBounds(imageRef, naturalWidth, naturalHeight);

  const getImageRect = useCallback(() => {
    return imageRef.current?.getBoundingClientRect() ?? null;
  }, [imageRef]);

  const applyResize = useCallback(
    (handle: HandlePosition, startCrop: CropRect, pct: { x: number; y: number }, startPct: { x: number; y: number }): CropRect => {
      const dx = pct.x - startPct.x;
      const dy = pct.y - startPct.y;
      let { topLeftX, topLeftY, bottomRightX, bottomRightY } = startCrop;

      if (handle.includes('w')) topLeftX = clamp(startCrop.topLeftX + dx, 0, bottomRightX - MIN_CROP_PERCENT);
      if (handle.includes('e')) bottomRightX = clamp(startCrop.bottomRightX + dx, topLeftX + MIN_CROP_PERCENT, 100);
      if (handle.includes('n')) topLeftY = clamp(startCrop.topLeftY + dy, 0, bottomRightY - MIN_CROP_PERCENT);
      if (handle.includes('s')) bottomRightY = clamp(startCrop.bottomRightY + dy, topLeftY + MIN_CROP_PERCENT, 100);

      let newCrop: CropRect = { topLeftX, topLeftY, bottomRightX, bottomRightY };
      if (aspectRatio != null) {
        newCrop = enforceAspectRatio(newCrop, aspectRatio, naturalWidth, naturalHeight);
      }
      return newCrop;
    },
    [aspectRatio, naturalWidth, naturalHeight],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, mode: 'move' | HandlePosition) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setIsDragging(true);

      if (mode === 'move') {
        dragRef.current = { type: 'move', startCrop: { ...crop }, startX: e.clientX, startY: e.clientY };
      } else {
        dragRef.current = { type: 'resize', handle: mode, startCrop: { ...crop }, startX: e.clientX, startY: e.clientY };
      }
    },
    [crop, disabled],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const rect = getImageRect();
      if (!rect) return;

      const currentPct = clientToPercent(e.clientX, e.clientY, rect, naturalWidth, naturalHeight);
      const startPct = clientToPercent(drag.startX, drag.startY, rect, naturalWidth, naturalHeight);

      if (drag.type === 'move') {
        const dx = currentPct.x - startPct.x;
        const dy = currentPct.y - startPct.y;
        const cropW = drag.startCrop.bottomRightX - drag.startCrop.topLeftX;
        const cropH = drag.startCrop.bottomRightY - drag.startCrop.topLeftY;

        let newTLX = drag.startCrop.topLeftX + dx;
        let newTLY = drag.startCrop.topLeftY + dy;

        newTLX = clamp(newTLX, 0, 100 - cropW);
        newTLY = clamp(newTLY, 0, 100 - cropH);

        onChange({
          topLeftX: newTLX,
          topLeftY: newTLY,
          bottomRightX: newTLX + cropW,
          bottomRightY: newTLY + cropH,
        });
      } else {
        onChange(applyResize(drag.handle, drag.startCrop, currentPct, startPct));
      }
    },
    [onChange, getImageRect, naturalWidth, naturalHeight, applyResize],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (dragRef.current) {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
        dragRef.current = null;
        setIsDragging(false);
      }
    },
    [],
  );

  const handleHandleDown = useCallback(
    (e: React.PointerEvent, handle: HandlePosition) => {
      handlePointerDown(e, handle);
    },
    [handlePointerDown],
  );

  // Within the image-bounds sub-rect, compute CSS positions as percentages
  const left = `${crop.topLeftX}%`;
  const top = `${crop.topLeftY}%`;
  const width = `${crop.bottomRightX - crop.topLeftX}%`;
  const height = `${crop.bottomRightY - crop.topLeftY}%`;

  return (
    <Box
      gc-testing-id="crop-overlay"
      position="absolute"
      // Position overlay exactly over the rendered image, not the container
      left={`${imageBounds.offsetX}px`}
      top={`${imageBounds.offsetY}px`}
      width={`${imageBounds.visibleW}px`}
      height={`${imageBounds.visibleH}px`}
      pointerEvents={disabled ? 'none' : 'auto'}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {/* Dimming regions */}
      <Box position="absolute" top={0} left={0} right={0} height={top} bg="rgba(0,0,0,0.5)" pointerEvents="none" />
      <Box position="absolute" bottom={0} left={0} right={0} top={`${crop.bottomRightY}%`} bg="rgba(0,0,0,0.5)" pointerEvents="none" />
      <Box position="absolute" top={top} left={0} width={left} height={height} bg="rgba(0,0,0,0.5)" pointerEvents="none" />
      <Box position="absolute" top={top} right={0} left={`${crop.bottomRightX}%`} height={height} bg="rgba(0,0,0,0.5)" pointerEvents="none" />

      {/* Crop region */}
      <Box
        gc-testing-id="crop-region"
        position="absolute"
        left={left}
        top={top}
        width={width}
        height={height}
        border="1px dashed"
        borderColor="white"
        opacity={disabled ? 0.5 : 1}
        cursor={disabled ? 'default' : isDragging ? 'grabbing' : 'move'}
        onPointerDown={(e) => handlePointerDown(e, 'move')}
      >
        {!disabled && (
          <>
            <CornerHandle position="nw" disabled={disabled} onPointerDown={handleHandleDown} />
            <CornerHandle position="ne" disabled={disabled} onPointerDown={handleHandleDown} />
            <CornerHandle position="sw" disabled={disabled} onPointerDown={handleHandleDown} />
            <CornerHandle position="se" disabled={disabled} onPointerDown={handleHandleDown} />
            <EdgeHandle position="n" disabled={disabled} onPointerDown={handleHandleDown} />
            <EdgeHandle position="s" disabled={disabled} onPointerDown={handleHandleDown} />
            <EdgeHandle position="w" disabled={disabled} onPointerDown={handleHandleDown} />
            <EdgeHandle position="e" disabled={disabled} onPointerDown={handleHandleDown} />
          </>
        )}
      </Box>
    </Box>
  );
}
